import {
  deleteDataItem,
  deleteWidget,
  deleteWidgetPage,
  deleteHistoryItem,
  deleteFolderLink,
  deleteFolder,
  createWidgetForPage,
  DEFAULT_WIDGET_PAGE_ID,
  getAllWidgets,
  getWidgetsByPage,
  getDataItem,
  addDataItem,
  addFolderLink,
  getFolderLinks,
  addHistoryItem,
  getAllFolders,
  updateWidget,
} from '@/lib/db';
import { emitDataChanged } from '@/hooks/useAppData';
import { getLastPreview, emitPreviewRequest } from '@/events/previewBus';
import { clearClipboardIfMatches } from '@/lib/clipboardUtils';
import type { SelectionEntry } from '@/hooks/useGlobalSelection';
import type { DataItem, FolderDataLink, HistoryItem } from '@/types';
import { toast } from 'sonner';

/** Duplicate a DataItem with a new ID */
export async function duplicateDataItem(dataId: string): Promise<DataItem | null> {
  const original = await getDataItem(dataId);
  if (!original) return null;
  const copy: DataItem = {
    ...original,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  await addDataItem(copy);
  return copy;
}

async function snapshotDeletedDataToHistory(dataId?: string): Promise<void> {
  if (!dataId) return;

  const copy = await duplicateDataItem(dataId);
  if (!copy) return;

  const historyEntry: HistoryItem = {
    id: crypto.randomUUID(),
    dataId: copy.id,
    createdAt: Date.now(),
  };
  await addHistoryItem(historyEntry);
}

async function snapshotFolderContentsToHistory(folderId: string): Promise<void> {
  const links = await getFolderLinks(folderId);
  for (const link of links) {
    await snapshotDeletedDataToHistory(link.dataId);
  }

  const folders = await getAllFolders();
  const childFolders = folders.filter((folder) => folder.parentId === folderId);
  for (const child of childFolders) {
    await snapshotFolderContentsToHistory(child.id);
  }
}

/** Delete selected items across all areas */
export async function deleteSelectedItems(entries: SelectionEntry[]): Promise<void> {
  const lastPreview = getLastPreview();
  let previewCleared = false;

  for (const entry of entries) {
    // 1. If what we're deleting is what we're currently previewing, clear preview area.
    if (lastPreview && !previewCleared) {
      if (lastPreview.id === entry.dataId) {
        emitPreviewRequest({ type: 'empty', content: '' });
        previewCleared = true;
      }
    }

    // 2. If the deleted content matches the current clipboard, clear clipboard.
    if (entry.dataId) {
      const dataItem = await getDataItem(entry.dataId);
      if (dataItem) {
        await clearClipboardIfMatches(dataItem.content);
      }
    }

    switch (entry.area) {
      case 'widget': {
        // itemId is the widget id
        await snapshotDeletedDataToHistory(entry.dataId);
        await deleteWidget(entry.itemId);
        await deleteDataItem(entry.dataId!);
        break;
      }
      case 'history': {
        await deleteHistoryItem(entry.itemId);
        break;
      }
      case 'folder': {
        // itemId could be a linkId (data item inside folder)
        // We delete the link and the data
        await snapshotDeletedDataToHistory(entry.dataId);
        await deleteFolderLink(entry.itemId);
        if (entry.dataId) {
          await deleteDataItem(entry.dataId);
        }
        break;
      }
      case 'folder-node': {
        // itemId is the folder id itself
        await snapshotFolderContentsToHistory(entry.itemId);
        await deleteFolder(entry.itemId);
        break;
      }
    }
  }
  emitDataChanged();
  toast.success(`${entries.length}개 항목 삭제 완료`);
}

/** Delete a widget page and snapshot its widget contents through the normal history recovery path. */
export async function deleteWidgetPageWithWidgets(pageId: string): Promise<number> {
  const widgets = await getWidgetsByPage(pageId);
  const lastPreview = getLastPreview();
  let previewCleared = false;

  for (const widget of widgets) {
    if (lastPreview && !previewCleared && lastPreview.id === widget.dataId) {
      emitPreviewRequest({ type: 'empty', content: '' });
      previewCleared = true;
    }

    const dataItem = await getDataItem(widget.dataId);
    if (dataItem) {
      await clearClipboardIfMatches(dataItem.content);
    }

    await snapshotDeletedDataToHistory(widget.dataId);
    await deleteWidget(widget.id);
    await deleteDataItem(widget.dataId);
  }

  await deleteWidgetPage(pageId);
  emitDataChanged();
  return widgets.length;
}

/** Copy (duplicate) selected items to a target area */
export async function copySelectedToArea(
  entries: SelectionEntry[],
  targetArea: 'widget' | 'folder',
  targetFolderId?: string,
  targetPageId = DEFAULT_WIDGET_PAGE_ID,
): Promise<void> {
  let count = 0;

  if (targetArea === 'widget') {
    const existing = await getWidgetsByPage(targetPageId);
    const minOrder = existing.length > 0 ? Math.min(...existing.map(w => w.order)) : 0;

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if (entry.area === 'folder-node') {
        const links = await getFolderLinks(entry.itemId);
        for (let j = 0; j < links.length; j++) {
          const copy = await duplicateDataItem(links[j].dataId);
          if (!copy) continue;
          await createWidgetForPage(copy.id, targetPageId, minOrder - 1 - (count++));
        }
      } else {
        const copy = await duplicateDataItem(entry.dataId!);
        if (!copy) continue;
        await createWidgetForPage(copy.id, targetPageId, minOrder - 1 - (count++));
      }
    }
  } else if (targetArea === 'folder' && targetFolderId) {
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if (entry.area === 'folder-node') {
        const links = await getFolderLinks(entry.itemId);
        for (let j = 0; j < links.length; j++) {
          const copy = await duplicateDataItem(links[j].dataId);
          if (!copy) continue;
          const newLink: FolderDataLink = {
            id: crypto.randomUUID(),
            folderId: targetFolderId,
            dataId: copy.id,
            order: Date.now() + (count++),
          };
          await addFolderLink(newLink);
        }
      } else {
        const copy = await duplicateDataItem(entry.dataId!);
        if (!copy) continue;
        const link: FolderDataLink = {
          id: crypto.randomUUID(),
          folderId: targetFolderId,
          dataId: copy.id,
          order: Date.now() + (count++),
        };
        await addFolderLink(link);
      }
    }
  }

  emitDataChanged();
  toast.success(`${count}개 항목 복제 완료`);
}

/** Move existing widgets to another widget page without duplicating their data. */
export async function moveSelectedWidgetsToPage(
  entries: SelectionEntry[],
  targetPageId: string,
): Promise<number> {
  const widgetEntries = entries.filter((entry) => entry.area === 'widget');
  if (widgetEntries.length === 0) return 0;

  const [allWidgets, targetWidgets] = await Promise.all([
    getAllWidgets(),
    getWidgetsByPage(targetPageId),
  ]);
  const widgetById = new Map(allWidgets.map((widget) => [widget.id, widget]));
  const selectedWidgets = widgetEntries
    .map((entry) => widgetById.get(entry.itemId))
    .filter((widget) => widget !== undefined && widget.pageId !== targetPageId);

  if (selectedWidgets.length === 0) return 0;

  const minOrder = targetWidgets.length > 0 ? Math.min(...targetWidgets.map((widget) => widget.order)) : 0;

  for (let i = 0; i < selectedWidgets.length; i++) {
    const widget = selectedWidgets[i];
    await updateWidget({
      ...widget,
      pageId: targetPageId,
      order: minOrder - 1 - i,
    });
  }

  emitDataChanged();
  toast.success(`${selectedWidgets.length}개 위젯을 페이지로 이동했습니다`);
  return selectedWidgets.length;
}

/** Move selected items to a target area (copy + delete originals) */
export async function moveSelectedToArea(
  entries: SelectionEntry[],
  targetArea: 'widget' | 'folder',
  targetFolderId?: string,
  targetPageId = DEFAULT_WIDGET_PAGE_ID,
): Promise<void> {
  await copySelectedToArea(entries, targetArea, targetFolderId, targetPageId);
  // Delete originals (but not the DataItem — just the link/widget/history entry)
  for (const entry of entries) {
    switch (entry.area) {
      case 'widget':
        await deleteWidget(entry.itemId);
        break;
      case 'history':
        await deleteHistoryItem(entry.itemId);
        break;
      case 'folder':
        await deleteFolderLink(entry.itemId);
        if (entry.dataId) {
          await deleteDataItem(entry.dataId);
        }
        break;
      case 'folder-node': {
        // Only delete the links inside, keep the folder node
        const links = await getFolderLinks(entry.itemId);
        for (const link of links) {
          await deleteFolderLink(link.id);
          await deleteDataItem(link.dataId);
        }
        break;
      }
    }
  }
  emitDataChanged();
  toast.success('이동 완료');
}
